import { Injectable } from '@angular/core';
import { SanityProductControl, SanityProductControlKey } from '../sanity-product-control';
import { SanityProductVariation } from '../sanity-product-variation';

/**
 * This service is used to determine matching product controls.
 */
@Injectable({ providedIn: 'root' })
export class SanityMatchingProductControlsService {
  /**
   * Get available product controls for the current product variation.
   *
   * @param productControls - List of all product controls.
   * @param productVariations - List of all product variations.
   * @param currentProductVariation - The current selected product variation.
   *
   * @returns A list of product controls with disabled options marked.
   */
  getAvailableProductControlsForCurrentVariation(
    productControls: SanityProductControl[],
    productVariations: SanityProductVariation[],
    currentProductVariation: SanityProductVariation,
  ): SanityProductControl[] {
    // Return empty array if any of the input arrays are empty
    if (!productControls?.length || !productVariations?.length) {
      return [];
    }

    // Use the first product variation if current is not provided
    if (!currentProductVariation) {
      currentProductVariation = productVariations[0];
    }

    // Get all control keys that are available for the current product variation
    // and that are also available in the list of all product controls
    const validControlKeys = Object.values(SanityProductControlKey).filter(
      k => productControls.some(control => control.key === k) && currentProductVariation[k],
    );

    // Map through each control to check which options are available
    return productControls.map(control => {
      // Evaluate the available options for each control
      const updatedOptions = control.options?.map(option => {
        // Create a variation object to look for in the list of all product variations
        const variantCriteria = {
          ...currentProductVariation,
          [control.key]: option.value,
        };

        // Check if such a product variation exists
        const doesVariantExist = productVariations.some(productVariation => {
          // Only compare keys that are meant to be control keys
          return validControlKeys.every(key => productVariation[key] === variantCriteria[key]);
        });

        return {
          ...option,
          disabled: !doesVariantExist,
        };
      });

      return {
        ...control,
        options: updatedOptions,
      };
    });
  }
}
